import '@/styles/globals.scss';
import '@fontsource-variable/funnel-display/wght.css';
import '@fontsource-variable/geist/wght.css';
import * as Sentry from '@sentry/react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { env } from '@/lib/env';

import App from './App';

if (env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    release: env.VITE_RELEASE,
    environment: env.VITE_ENVIRONMENT,
    tracesSampleRate: 0.1,
    integrations: [Sentry.browserTracingIntegration()],
  });
}

// Starts the in-browser MSW worker so flows can be exercised without a backend.
// Gated solely on the flags (not import.meta.env.DEV) so it also works in a
// production build — QA, where the backend isn't deployed yet. VITE_MOCK_API
// mocks the whole backend (auth + feature APIs, seeded); VITE_MOCK_AUTH mocks
// only /auth/*. Keep both flags unset everywhere except QA, and remove them once
// the backend is live. The worker chunk is dynamically imported, so nothing ships
// unless a flag is on.
async function enableMockBackend() {
  const mockApi = import.meta.env.VITE_MOCK_API === 'true';
  const mockAuth = import.meta.env.VITE_MOCK_AUTH === 'true';
  if (!mockApi && !mockAuth) return;

  const { worker, seedMockBackend } = await import('@/test/msw/browser');
  if (mockApi) seedMockBackend();
  await worker.start({ onUnhandledRequest: 'bypass' });

  // Keep mocking alive across service-worker restarts. The MSW worker tracks its
  // active clients in an in-memory Set; when the browser evicts/restarts the SW
  // (background-tab timer throttling starves MSW's 5s keepalive, or DevTools
  // "Stop"), that Set is emptied and the restarted SW passes *every* request
  // through to the real, undeployed backend — the "works only after a manual
  // reload" symptom. MSW's keepalive wakes the SW but never re-sends
  // MOCK_ACTIVATE, and calling worker.start() again is a no-op. So we re-post
  // MOCK_ACTIVATE ourselves — the same idempotent message MSW sends at startup;
  // the SW just re-adds this client (Set.add), so repeated posts are free.
  // QA-mock-only path; removed once the backend is live.
  const reactivateMock = () => {
    navigator.serviceWorker?.controller?.postMessage('MOCK_ACTIVATE');
  };
  // Recover instantly when returning to a backgrounded tab, and poll on the same
  // 5s cadence as MSW's keepalive to cover eviction while the tab stays focused.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') reactivateMock();
  });
  window.setInterval(reactivateMock, 5_000);
}

await enableMockBackend();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
